import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Card,
  Flex,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  SimpleGrid,
  Spacer,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight, FiSearch } from "react-icons/fi";
import { BiArrowBack } from "react-icons/bi";
import CustomButton from "../../components/ButtonComp";
import DisplayData from "../../components/DisplayData";
import Loader from "../../components/Loader";
import ImageModal from "../../components/ImageModal";
import { get_all_quotation_details } from "../../redux/quotationSlice";
import { formatCurrency } from "../../utils/utils";
import { get_all_purchaseorder_details } from "../../redux/purchaseOrderSlice";

const PurchaseOrderDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // Get data from Redux store
  const {  all_purchaseorder_list, all_loading } = useSelector(state => state.purchaseOrder);
  
  const [data, setData] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [rows, setRows] = useState(10);
  const [page, setPage] = useState(1);
  const [initialValue, setInitialValue] = useState(1);
  const { onOpen, onClose, isOpen } = useDisclosure();
  const [url, setUrl] = useState("");
  const [settingData, setSettingData] = useState({});

  useEffect(() => {
    // Get the ID from location.state
    const purchaseOrderId = location.state?.id || location.state;
    
    if (purchaseOrderId) {
      dispatch(get_all_purchaseorder_details(purchaseOrderId));
    }
  }, [dispatch, location.state]);

  // Update local state when Redux state changes
  useEffect(() => {
    if ( all_purchaseorder_list && all_purchaseorder_list.length > 0) {
      const quotationData = all_purchaseorder_list[0];
      setData(quotationData);
      setSettingData({
        credit_days: quotationData.credit_days || "",
        credit_limit: quotationData.credit_limit || "",
      });
    }
  }, [all_purchaseorder_list]);

  // Filter products based on search query
  const filteredProducts = (data?.products || []).filter(
    (product) =>
      product &&
      (
        product.product_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.category?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.type?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        product.productid?.toLowerCase().includes(searchQuery.toLowerCase())
      )
  );

  // Pagination for products
  const totalProducts = filteredProducts.length;
  const startIndex = (page - 1) * rows;
  const endIndex = startIndex + rows;
  const paginatedProducts = filteredProducts.slice(startIndex, endIndex);

  const onPrevious = () => {
    if (page > 1) {
      setPage(page - 1);
      setInitialValue(initialValue - rows);
    }
  };

  const onNext = () => {
    if (rows * page < totalProducts) {
      setPage(page + 1);
      setInitialValue(initialValue + rows);
    }
  };

  const calculateTotalPrice = (price, qty) => {
    return price && qty ? (parseFloat(price) * parseFloat(qty)).toFixed(2) : '0.00';
  };

  return (
    <>
      {/* Header */}
      <Card
        display={"flex"}
        flexDirection={"row"}
        alignItems={"center"}
        justify={"space-between"}
        paddingInline={5}
        paddingBlock={2}
        mb={4}
      >
        <Heading fontSize={"xl"}>Purchase Order Details</Heading>
        <Flex

  gap={2}
  align="center"
  direction={["column", "column", "row"]}
  p={4}
  flexWrap="wrap"
>
  {/* Rows Select */}
  <Select
    size="md"
    value={rows}
    onChange={(e) => {
      setRows(Number(e.target.value));
      setPage(1);
      setInitialValue(1);
    }}
    w={["100%", "100%", "auto"]}
  >
    <option value="5">5</option>
    <option value="10">10</option>
    <option value="25">25</option>
    <option value="50">50</option>
    <option value="100">100</option>
  </Select>

  {/* Search Box */}
  <InputGroup
    bg="#f4f5f7"
    borderRadius={5}
    w={["100%", "100%", "auto"]}
    maxW="sm"
  >
    <Input
      placeholder="Search products..."
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
    />
    <InputRightElement>
      <FiSearch />
    </InputRightElement>
  </InputGroup>

  {/* Back Button */}
  <CustomButton
    icon={<BiArrowBack color="#fff" />}
    title={"Back"}
    height={"40px"}
    b_color={"#000"}
    onClick={() => navigate(-1)}
  />
</Flex>
      </Card>

      {all_loading ? (
        <Card p={5} justify={"center"} alignItems={"center"}>
          <Loader />
        </Card>
      ) : (
        <>
          {/* Quotation Basic Info */}


          {/* Products Table */}
          <Card mb={4}>
            {/* Table Header with Search and Controls */}

            {/* Products Table */}
            <TableContainer>
              <Table variant="simple" size="md">
                <Thead bg="#f7f7f7">
                  <Tr>
                    <Th>Category</Th>
                    <Th>Type</Th>
                    <Th>Product Name</Th>
                    <Th isNumeric>Unit Price</Th>
                    <Th isNumeric>Available Qty</Th>
                    <Th isNumeric>Total Qty</Th>
                    <Th isNumeric>Total Amount</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedProducts.length === 0 ? (
                    <Tr>
                      <Td colSpan={8} textAlign="center">
                        No products found
                      </Td>
                    </Tr>
                  ) : (
                    paginatedProducts.map((product, i) => (
                      <Tr key={i} _hover={{ bg: "#f9f9f9" }}>
                        <Td>{product.category || 'NA'}</Td>
                        <Td>{product.type || 'NA'}</Td>
                        <Td>{product.product_name || 'NA'}</Td>
                        <Td isNumeric>₹{product.price || '0.00'}</Td>
                        <Td isNumeric>{product.qty || 'NA'}</Td>
                        <Td isNumeric>{data?.total_quantity || 'NA'}</Td>
                        <Td isNumeric>
                        {product.qty && product.price 
                        ? `₹${(parseFloat(product.qty) * parseFloat(product.price)).toFixed(2)}`
                               : 'NA'
                        }
                      </Td>
                      </Tr>
                    ))
                  )}
                </Tbody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalProducts > 0 && (
              <Card
                alignItems={"center"}
                gap={3}
                flexDirection={"row"}
                mt={4}
                justifyContent={"center"}
                padding={3}
              >
                <FiChevronLeft
                  cursor={"pointer"}
                  color={page === 1 ? "gray" : "#007bc9"}
                  size={35}
                  onClick={onPrevious}
                />
                <Flex gap={3} alignItems={"center"}>
                  <Text>{startIndex + 1}</Text>-
                  <Text>{Math.min(endIndex, totalProducts)}</Text>
                  <Text>of</Text>
                  <Text>{totalProducts}</Text>
                </Flex>
                <FiChevronRight
                  cursor={"pointer"}
                  color={endIndex >= totalProducts ? "gray" : "#007bc9"}
                  size={35}
                  onClick={onNext}
                />
              </Card>
            )}
          </Card>

          {/* Additional Information */}
        </>
      )}
      <ImageModal url={url} onClose={onClose} isOpen={isOpen} />
    </>
  );
};

export default PurchaseOrderDetail;